import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Card,
  CardContent
} from '@mui/material';
import { format } from 'date-fns';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { generateClient } from 'aws-amplify/api';
import { getUrl } from '@aws-amplify/storage';

const API = generateClient();

const ReceivedFiles = ({ documentRequestID, selectedClient }) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (documentRequestID) {
      fetchUploadedFiles();
    }
  }, [documentRequestID]);

  const fetchUploadedFiles = async () => {
    setLoading(true);
    try {
      const response = await API.graphql({
        query: /* GraphQL */ `
          query DocumentUploadByRequest($documentRequestID: String!) {
            documentUploadByRequest(documentRequestID: $documentRequestID) {
              items {
                id
                fileName
                fileSize
                fileType
                category
                uploadedAt
                uploadedBy
                annotation
                status
                validationStatus
                validationNotes
                validatedBy
                validatedAt
                s3Key
                s3Bucket
                documentRequest {
                  vendorName
                  associatedCertificateIds
                  certificateDetails
                }
              }
            }
          }
        `,
        variables: { documentRequestID }
      });

      const uploadedFiles = response?.data?.documentUploadByRequest?.items || [];
      setFiles(uploadedFiles);
    } catch (error) {
      console.error('Error fetching uploaded files:', error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getValidationStatus = (status) => {
    const statusConfig = {
      APPROVED: { 
        label: 'Approved', 
        color: 'success', 
        icon: <CheckCircleIcon fontSize="small" /> 
      },
      REJECTED: { 
        label: 'Rejected', 
        color: 'error', 
        icon: <CancelIcon fontSize="small" /> 
      },
      PENDING_REVIEW: { 
        label: 'Pending Review', 
        color: 'warning', 
        icon: <HourglassEmptyIcon fontSize="small" /> 
      },
      UPLOADED: { 
        label: 'Uploaded', 
        color: 'default', 
        icon: <InsertDriveFileIcon fontSize="small" /> 
      }
    };

    const config = statusConfig[status] || statusConfig.UPLOADED;
    
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const handlePreview = async (file) => {
    // Check if file type supports preview
    const previewableTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    
    if (!previewableTypes.includes(file.fileType)) {
      // If not previewable, download instead
      handleDownload(file);
      return;
    }

    setSelectedFile(file);
    setPreviewOpen(true);
    
    try {
      // Get signed URL for preview
      const urlResult = await getUrl({
        path: file.s3Key,
        options: {
          expiresIn: 3600 // 1 hour expiry
        }
      });
      
      setPreviewUrl(urlResult.url.href);
    } catch (error) {
      console.error('Error getting preview URL:', error);
      setPreviewUrl('');
    }
  };

  const handleDownload = async (file) => {
    try {
      const urlResult = await getUrl({
        path: file.s3Key,
        options: {
          expiresIn: 300 // 5 minutes expiry for download
        }
      });
      
      // Create a temporary link and click it
      const link = document.createElement('a');
      link.href = urlResult.url.href;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const filteredFiles = files.filter(file => 
    file.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCertificateInfo = (file) => {
    if (!file.documentRequest?.associatedCertificateIds?.length) {
      return '-';
    }
    
    const certificateDetails = file.documentRequest.certificateDetails;
    if (certificateDetails && typeof certificateDetails === 'string') {
      try {
        const details = JSON.parse(certificateDetails);
        if (details.length > 0 && details[0].insuredName) {
          return details[0].insuredName;
        }
      } catch (e) {
        console.error('Error parsing certificate details:', e);
      }
    }
    
    return `${file.documentRequest.associatedCertificateIds.length} certificate(s)`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!documentRequestID) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Select an email from the Sent Email tab to view received files
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Received Files ({filteredFiles.length})
            </Typography>
            <TextField
              size="small"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          </Stack>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Upload Date</TableCell>
              <TableCell>Certificate</TableCell>
              <TableCell>Validation Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No files received yet
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredFiles
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((file, index) => (
                  <TableRow key={file.id || index} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <InsertDriveFileIcon fontSize="small" color="action" />
                        <Typography variant="body2">{file.fileName}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={file.category.replace('_', ' ')} 
                        size="small" 
                        variant="outlined" 
                      />
                    </TableCell>
                    <TableCell>{formatFileSize(file.fileSize)}</TableCell>
                    <TableCell>
                      {file.uploadedAt ? (
                        <Stack>
                          <Typography variant="body2">
                            {format(new Date(file.uploadedAt), 'MMM dd, yyyy')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(file.uploadedAt), 'h:mm a')}
                          </Typography>
                        </Stack>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getCertificateInfo(file)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getValidationStatus(file.validationStatus || file.status)}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Preview">
                          <IconButton 
                            size="small" 
                            onClick={() => handlePreview(file)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDownload(file)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        {filteredFiles.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredFiles.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        )}
      </TableContainer>

      {/* File Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedFile?.fileName}
            </Typography>
            <IconButton onClick={handleClosePreview}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {previewUrl ? (
            selectedFile?.fileType === 'application/pdf' ? (
              <iframe
                src={previewUrl}
                width="100%"
                height="600px"
                title="PDF Preview"
                style={{ border: 'none' }}
              />
            ) : selectedFile?.fileType?.startsWith('image/') ? (
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={previewUrl}
                  alt={selectedFile?.fileName}
                  style={{ maxWidth: '100%', maxHeight: '600px', objectFit: 'contain' }}
                />
              </Box>
            ) : (
              <Typography>Preview not available for this file type</Typography>
            )
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          
          {selectedFile?.annotation && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Annotation:
              </Typography>
              <Typography variant="body2">
                {selectedFile.annotation}
              </Typography>
            </Box>
          )}
          
          {selectedFile?.validationNotes && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Validation Notes:
              </Typography>
              <Typography variant="body2">
                {selectedFile.validationNotes}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReceivedFiles;